import { useEffect, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { LessonMap } from "@/components/LessonMap";
import {
  X, GitBranch, Footprints, TrendingUp, Sparkles, AlertTriangle, MapPin,
  Check, Crown, ArrowRight, PlayCircle,
} from "lucide-react";

// Demo data only — learners are anonymised. Each learner has a distinct
// dataset for both practice modes (recurring errors / retrace last lesson).
const LEARNERS = [
  {
    id: "a",
    label: "Learner A",
    trend: "Improving",
    stats: [["6", "Lessons"], ["5.2", "Avg errors"], ["0", "Best"]],
    note: "Learner A did really well out there today! During the next practice, it's worth giving some extra attention to mirror checks and head checks for blind spots — these came up a few times along the route. Quiet local streets are perfect for building the habit through repetition.",
    recurring: {
      mapCaption: "Recurring errors · top spots",
      pins: [
        { code: "1", color: "#2563EB", x: "50%", y: "40%" },
        { code: "3", color: "#E8890C", x: "52%", y: "50%" },
        { code: "NP2L", color: "#E8368F", x: "44%", y: "60%" },
        { code: "6", color: "#8A4B1A", x: "66%", y: "56%" },
      ],
      watch: [
        { n: 1, title: "Missed Mirror Check", where: "Near 14–18 Pembroke St, Kensington Park", x: "×3" },
        { n: 2, title: "Missed Head Check / Blind Spot", where: "Near 237 Glynburn Rd, St Morris", x: "×2" },
        { n: 3, title: "Missed Mirror Check", where: "Near 96–100 Saint Bernards Rd, Magill", x: "×2" },
      ],
      checklist: [
        { title: "Not as Practicable to the Left", where: "Near 311 Kensington Rd, Kensington Park" },
        { title: "Missed Mirror Check", where: "Near 329 Kensington Rd, Kensington Park" },
        { title: "Not Giving Way to a Pedestrian", where: "Near 289–291 Glynburn Rd, St Morris" },
        { title: "Poor Lane Position", where: "Near 96–100 Saint Bernards Rd, Magill" },
        { title: "Missed Head Check / Blind Spot", where: "Near 237 Glynburn Rd, St Morris" },
        { title: "Harsh / Late Braking", where: "Near 14–18 Pembroke St, Kensington Park" },
      ],
    },
    retrace: {
      mapCaption: "Retrace last lesson · 4 error spots",
      pins: [
        { code: "2", color: "#2563EB", x: "42%", y: "32%" },
        { code: "4", color: "#E8368F", x: "58%", y: "46%" },
        { code: "5", color: "#E8890C", x: "47%", y: "63%" },
        { code: "7", color: "#8A4B1A", x: "65%", y: "55%" },
      ],
      watch: [
        { n: 1, title: "Not as Practicable to the Left", where: "Near 311 Kensington Rd, Kensington Park", x: "×1" },
        { n: 2, title: "Not Giving Way to a Pedestrian", where: "Near 289–291 Glynburn Rd, St Morris", x: "×1" },
        { n: 3, title: "Harsh / Late Braking", where: "Near 14–18 Pembroke St, Kensington Park", x: "×1" },
      ],
      checklist: [
        { title: "Pull up and park on the left", where: "Near 311 Kensington Rd, Kensington Park" },
        { title: "Pedestrian crossing approach", where: "Near 289–291 Glynburn Rd, St Morris" },
        { title: "Smooth braking to a stop", where: "Near 14–18 Pembroke St, Kensington Park" },
        { title: "Mirror check before turning", where: "Near 329 Kensington Rd, Kensington Park" },
      ],
    },
  },
  {
    id: "b",
    label: "Learner B",
    trend: "Building",
    stats: [["3", "Lessons"], ["7.4", "Avg errors"], ["4", "Best"]],
    note: "A solid start on the coastal roads. Roundabout judgement is coming along, but speed control coming off the esplanade needs work — keep practising the 40 km/h zones around the foreshore, especially in school times.",
    recurring: {
      mapCaption: "Recurring errors · top spots",
      pins: [
        { code: "1", color: "#E8368F", x: "38%", y: "46%" },
        { code: "2", color: "#2563EB", x: "55%", y: "36%" },
        { code: "4", color: "#E8890C", x: "60%", y: "58%" },
        { code: "5", color: "#8A4B1A", x: "44%", y: "66%" },
      ],
      watch: [
        { n: 1, title: "Rolling Through Stop Sign", where: "Near Jetty Rd & Moseley St, Glenelg", x: "×3" },
        { n: 2, title: "Speeding — 40 Zone", where: "Near Brighton Rd, Brighton", x: "×2" },
        { n: 3, title: "Wide Right Turn", where: "Near Edwards St, South Brighton", x: "×2" },
      ],
      checklist: [
        { title: "Full stop at stop sign", where: "Near Jetty Rd & Moseley St, Glenelg" },
        { title: "Hold 40 km/h past the foreshore", where: "Near Brighton Rd, Brighton" },
        { title: "Slow in, power out roundabout", where: "Near Broadway, Glenelg South" },
        { title: "Tight right turn entry", where: "Near Edwards St, South Brighton" },
        { title: "Mirror and signal before lane change", where: "Near Tapleys Hill Rd, Glenelg North" },
      ],
    },
    retrace: {
      mapCaption: "Retrace last lesson · 4 error spots",
      pins: [
        { code: "1", color: "#2563EB", x: "52%", y: "42%" },
        { code: "3", color: "#E8890C", x: "40%", y: "55%" },
        { code: "6", color: "#E8368F", x: "62%", y: "50%" },
        { code: "8", color: "#8A4B1A", x: "50%", y: "68%" },
      ],
      watch: [
        { n: 1, title: "Rolling Through Stop Sign", where: "Near Pier St & Adelphi Tce, Glenelg North", x: "×1" },
        { n: 2, title: "Late Indication", where: "Near 478 Brighton Rd, Brighton", x: "×1" },
        { n: 3, title: "Hesitation at Roundabout", where: "Near Broadway & Partridge St, Glenelg South", x: "×1" },
      ],
      checklist: [
        { title: "Stop sign full stop", where: "Near Pier St & Adelphi Tce, Glenelg North" },
        { title: "Signal 30 m before the turn", where: "Near 478 Brighton Rd, Brighton" },
        { title: "Confident roundabout entry", where: "Near Broadway, Glenelg South" },
        { title: "Speed check downhill", where: "Near Esplanade, Somerton Park" },
      ],
    },
  },
  {
    id: "c",
    label: "Learner C",
    trend: "Test ready",
    stats: [["9", "Lessons"], ["3.1", "Avg errors"], ["1", "Best"]],
    note: "Nearly there — city driving is looking confident. The main thing to polish before the test is lane discipline on multi-lane roads, and checking the blind spot when moving back left after passing parked cars on The Parade.",
    recurring: {
      mapCaption: "Recurring errors · top spots",
      pins: [
        { code: "2", color: "#2563EB", x: "46%", y: "38%" },
        { code: "3", color: "#8A4B1A", x: "58%", y: "52%" },
        { code: "4", color: "#E8890C", x: "50%", y: "62%" },
        { code: "NP1R", color: "#E8368F", x: "38%", y: "52%" },
      ],
      watch: [
        { n: 1, title: "Missed Head Check / Blind Spot", where: "Near The Parade, Norwood", x: "×2" },
        { n: 2, title: "Poor Lane Position", where: "Near Fullarton Rd, Kent Town", x: "×2" },
        { n: 3, title: "Late Indication", where: "Near Magill Rd, Stepney", x: "×1" },
      ],
      checklist: [
        { title: "Head check before merging", where: "Near The Parade, Norwood" },
        { title: "Centre the lane on multi-lane roads", where: "Near Fullarton Rd, Kent Town" },
        { title: "Signal earlier on approach", where: "Near Magill Rd, Stepney" },
        { title: "Scan for cyclists at the lights", where: "Near Rundle St, Kent Town" },
        { title: "Gap selection turning right", where: "Near Portrush Rd, Norwood" },
      ],
    },
    retrace: {
      mapCaption: "Retrace last lesson · 4 error spots",
      pins: [
        { code: "1", color: "#2563EB", x: "54%", y: "34%" },
        { code: "2", color: "#E8890C", x: "44%", y: "48%" },
        { code: "5", color: "#E8368F", x: "60%", y: "60%" },
        { code: "7", color: "#8A4B1A", x: "42%", y: "64%" },
      ],
      watch: [
        { n: 1, title: "Missed Head Check", where: "Near 161 The Parade, Norwood", x: "×1" },
        { n: 2, title: "Wide Left Turn", where: "Near College Rd, Kent Town", x: "×1" },
        { n: 3, title: "Creeping at the Stop Line", where: "Near Dequetteville Tce, Kent Town", x: "×1" },
      ],
      checklist: [
        { title: "Blind spot check past parked cars", where: "Near 161 The Parade, Norwood" },
        { title: "Tight left turn setup", where: "Near College Rd, Kent Town" },
        { title: "Hold behind the stop line", where: "Near Dequetteville Tce, Kent Town" },
        { title: "Cross tram tracks at an angle", where: "Near Kensington Rd, Norwood" },
      ],
    },
  },
  {
    id: "d",
    label: "Learner D",
    trend: "Steady",
    stats: [["4", "Lessons"], ["6.0", "Avg errors"], ["2", "Best"]],
    note: "A great first freeway session — merging is much smoother now. Next practice, keep working on downhill speed control on the South Eastern Freeway and leaving a bigger gap in the 80 km/h zones through Stirling.",
    recurring: {
      mapCaption: "Recurring errors · top spots",
      pins: [
        { code: "1", color: "#8A4B1A", x: "48%", y: "44%" },
        { code: "3", color: "#2563EB", x: "58%", y: "38%" },
        { code: "5", color: "#E8890C", x: "52%", y: "58%" },
        { code: "6", color: "#E8368F", x: "40%", y: "56%" },
      ],
      watch: [
        { n: 1, title: "Following Too Closely", where: "Near Mount Barker Rd, Stirling", x: "×3" },
        { n: 2, title: "Speeding on Descent", where: "Near South Eastern Fwy, Crafers", x: "×2" },
        { n: 3, title: "Late Merge Signal", where: "Near Eagle on the Hill", x: "×1" },
      ],
      checklist: [
        { title: "Keep a 3-second gap at 80 km/h", where: "Near Mount Barker Rd, Stirling" },
        { title: "Gear down before the descent", where: "Near South Eastern Fwy, Crafers" },
        { title: "Signal early before merging", where: "Near Eagle on the Hill" },
        { title: "Mirror check before overtaking", where: "Near Mount Barker Rd, Verdun" },
        { title: "Steady throttle in 60 zones", where: "Near Main St, Stirling" },
      ],
    },
    retrace: {
      mapCaption: "Retrace last lesson · 4 error spots",
      pins: [
        { code: "2", color: "#E8890C", x: "52%", y: "36%" },
        { code: "4", color: "#2563EB", x: "44%", y: "52%" },
        { code: "6", color: "#E8368F", x: "58%", y: "56%" },
        { code: "8", color: "#8A4B1A", x: "48%", y: "66%" },
      ],
      watch: [
        { n: 1, title: "Speeding on Descent", where: "Near South Eastern Fwy, Crafers", x: "×1" },
        { n: 2, title: "Following Too Closely", where: "Near Mount Barker Rd, Stirling", x: "×1" },
        { n: 3, title: "Missed Mirror Check", where: "Near Avenue Rd, Stirling", x: "×1" },
      ],
      checklist: [
        { title: "Hold descent speed at 80", where: "Near South Eastern Fwy, Crafers" },
        { title: "Leave a 3-second gap", where: "Near Mount Barker Rd, Stirling" },
        { title: "Mirror before slowing", where: "Near Avenue Rd, Stirling" },
        { title: "Calm hill start", where: "Near Milan Tce, Stirling" },
      ],
    },
  },
];

export default function SupervisorDemo() {
  const [learnerId, setLearnerId] = useState("a");
  const [mode, setMode] = useState("recurring"); // recurring | retrace

  const learner = LEARNERS.find((l) => l.id === learnerId);
  const view = learner[mode];
  const checklist = view.checklist;

  const [checked, setChecked] = useState(() => Array(checklist.length).fill(false));

  // Reset ticks whenever the learner or practice mode changes
  useEffect(() => {
    setChecked(Array(checklist.length).fill(false));
  }, [learnerId, mode, checklist.length]);

  const done = checked.filter(Boolean).length;
  const toggle = (i) => {
    if (navigator.vibrate) navigator.vibrate(12);
    setChecked((c) => c.map((v, idx) => (idx === i ? !v : v)));
  };
  const reset = () => { if (navigator.vibrate) navigator.vibrate(8); setChecked(Array(checklist.length).fill(false)); };

  return (
    <section id="supervisor-demo" data-testid="supervisor-demo-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#F5A623]/40 bg-[#F5A623]/10 px-4 py-1.5 overline text-[#B57608]">
              <PlayCircle size={13} /> Interactive demo · Supervisor Mode
            </span>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.02] text-[#2B2B2B]">
              The supervisor's dashboard.
            </h2>
            <p className="mt-5 text-lg text-[#2B2B2B]/65 leading-relaxed">
              Parents and supervising drivers see the last completed lesson, or the recurring errors, so they
              know exactly what to work on in practice drives. It has notes from the instructor, maps that
              reflect either the most recurring errors or the last lesson and a checklist for your practise
              session. Try it: switch learners, pick a practice mode and tick off the checklist.
            </p>
          </div>
        </Reveal>

        {/* landscape tablet */}
        <Reveal delay={0.1}>
          <div className="mt-10 rounded-[2rem] border-[12px] border-[#151614] bg-[#151614] shadow-2xl overflow-hidden">
            <div className="relative bg-white rounded-[1.1rem] overflow-hidden">
              {/* header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-3">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#12A594] text-white">
                    <svg viewBox="0 0 100 108" width="22" height="24" aria-hidden="true"><path d="M50 4C29 4 12 21 12 42c0 26 38 54 38 54s38-28 38-54C88 21 71 4 50 4z" fill="#0f2e2b" /><path d="M35 44 l11 11 l20 -22" fill="none" stroke="#fff" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                  <div>
                    <p className="font-display font-extrabold text-lg text-[#1a1a1a] leading-none">Supervising mode</p>
                    <p className="text-sm text-[#1a1a1a]/50 mt-1" data-testid="sup-active-learner">{learner.label} · demo data</p>
                  </div>
                </div>
                <span className="grid place-items-center w-9 h-9 rounded-full bg-[#F1F2F3] text-[#2B2B2B]"><X size={17} /></span>
              </div>

              {/* learner tabs */}
              <div className="flex items-center gap-2 px-5 pb-3 overflow-x-auto no-scrollbar">
                <span className="text-xs font-semibold text-[#1a1a1a]/40 uppercase tracking-widest mr-1 shrink-0">Learner</span>
                {LEARNERS.map((l) => (
                  <button
                    key={l.id}
                    data-testid={`sup-learner-${l.id}`}
                    onClick={() => setLearnerId(l.id)}
                    className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                      learnerId === l.id ? "bg-[#12A594] text-white" : "bg-[#F1F2F3] text-[#1a1a1a]/70 hover:bg-[#E7E9EA]"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              {/* warning banner */}
              <div className="flex items-center gap-2 bg-[#FBF5E6] border-y border-[#F0E4C8] px-5 py-2.5 text-[13px] font-semibold text-[#9A6A12]">
                <AlertTriangle size={15} /> Supervisor use only — set up before you drive, never while driving.
              </div>

              {/* two columns */}
              <div className="grid lg:grid-cols-2 gap-4 p-4 lg:p-5 max-h-[560px] overflow-y-auto no-scrollbar">
                {/* LEFT */}
                <div className="space-y-4">
                  {/* progress */}
                  <div className="rounded-2xl border border-[#EAEAEA] p-5">
                    <p className="text-[11px] uppercase tracking-widest text-[#1a1a1a]/40 font-bold">Progress</p>
                    <div className="mt-3 flex items-center gap-3">
                      <span className="grid place-items-center w-14 h-14 rounded-xl bg-[#12A594] text-white"><TrendingUp size={24} /></span>
                      <span className="font-display font-extrabold text-3xl text-[#1a1a1a]" data-testid="sup-trend">{learner.trend}</span>
                    </div>
                    <div className="mt-5 grid grid-cols-3 text-center">
                      {learner.stats.map(([v, l]) => (
                        <div key={l}>
                          <p className="font-display font-extrabold text-3xl text-[#12A594] leading-none">{v}</p>
                          <p className="mt-1.5 text-xs text-[#1a1a1a]/55">{l}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* coach's note */}
                  <div className="rounded-2xl border border-[#BFE7E0] bg-[#F1FAF8] p-5">
                    <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#0d7d70] font-bold"><Sparkles size={13} /> Coach's note</p>
                    <p className="mt-3 text-[14px] text-[#1a1a1a]/75 leading-relaxed" data-testid="sup-coach-note">{learner.note}</p>
                  </div>

                  {/* watch for */}
                  <div className="rounded-2xl border border-[#EAEAEA] p-5">
                    <p className="text-[11px] uppercase tracking-widest text-[#1a1a1a]/40 font-bold">Watch for</p>
                    <div className="relative mt-3 h-44 rounded-xl overflow-hidden border border-[#EAEAEA]">
                      <LessonMap key={`${learnerId}-${mode}`} pins={view.pins} />
                      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold text-[#1a1a1a]/70 shadow" data-testid="sup-map-caption">
                        <MapPin size={11} className="text-[#12A594]" />
                        {view.mapCaption}
                      </span>
                    </div>
                    <div className="mt-3 divide-y divide-[#EFEFEF]" data-testid="sup-watch-list">
                      {view.watch.map((r) => (
                        <div key={`${learnerId}-${mode}-${r.n}`} className="flex items-center gap-3 py-2.5">
                          <span className="grid place-items-center w-7 h-7 rounded-full bg-[#2563EB] text-white text-xs font-bold shrink-0">{r.n}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold text-[#1a1a1a] leading-tight">{r.title}</p>
                            <p className="text-[11px] text-[#1a1a1a]/45 truncate">{r.where}</p>
                          </div>
                          <span className="text-[11px] font-bold text-[#1a1a1a]/40">{r.x}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* RIGHT */}
                <div className="space-y-4">
                  {/* practice mode */}
                  <div className="rounded-2xl border border-[#EAEAEA] p-5">
                    <p className="text-[11px] uppercase tracking-widest text-[#1a1a1a]/40 font-bold">Practice mode</p>
                    <div className="mt-3 grid grid-cols-2 gap-3">
                      {[
                        { key: "recurring", icon: GitBranch, title: "Recurring errors", desc: "Focus on trouble spots across all lessons" },
                        { key: "retrace", icon: Footprints, title: "Retrace last lesson", desc: "Drive the same route and rehearse each error" },
                      ].map((m) => {
                        const active = mode === m.key;
                        return (
                          <button
                            key={m.key}
                            data-testid={`sup-mode-${m.key}`}
                            onClick={() => setMode(m.key)}
                            className={`rounded-xl p-3.5 text-left transition-colors ${active ? "bg-[#12A594] text-white" : "bg-white border border-[#EAEAEA] hover:border-[#12A594]/50"}`}
                          >
                            <m.icon size={18} className={active ? "text-white/90" : "text-[#1a1a1a]/40"} />
                            <p className={`mt-2 text-[13px] font-bold ${active ? "text-white" : "text-[#1a1a1a]"}`}>{m.title}</p>
                            <p className={`mt-1 text-[11px] leading-snug ${active ? "text-white/80" : "text-[#1a1a1a]/50"}`}>{m.desc}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* session checklist */}
                  <div className="rounded-2xl border border-[#EAEAEA] p-5">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] uppercase tracking-widest text-[#1a1a1a]/40 font-bold">Session checklist</p>
                      <button data-testid="sup-checklist-reset" onClick={reset} className="text-[13px] font-bold text-[#12A594] hover:text-[#0d7d70]">Reset</button>
                    </div>
                    <div className="mt-3 h-2 w-full rounded-full bg-[#EFEFEF] overflow-hidden">
                      <div className="h-full rounded-full bg-[#12A594] transition-all duration-300" style={{ width: `${(done / checklist.length) * 100}%` }} />
                    </div>
                    <p className="mt-2 text-[13px] text-[#1a1a1a]/55" data-testid="sup-checklist-count">{done} of {checklist.length} practised</p>
                    <div className="mt-3 space-y-2.5" data-testid="sup-checklist">
                      {checklist.map((c, i) => {
                        const on = checked[i];
                        return (
                          <button
                            key={`${learnerId}-${mode}-${i}`}
                            data-testid={`sup-check-${i}`}
                            onClick={() => toggle(i)}
                            className={`w-full flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${on ? "border-[#12A594] bg-[#F1FAF8]" : "border-[#EAEAEA] hover:border-[#12A594]/40"}`}
                          >
                            <span className={`grid place-items-center w-7 h-7 rounded-lg shrink-0 transition-colors ${on ? "bg-[#12A594] text-white" : "border-2 border-[#D5D7D9]"}`}>
                              {on && <Check size={15} strokeWidth={3} />}
                            </span>
                            <div className="flex-1">
                              <p className={`text-[14px] font-bold leading-tight ${on ? "text-[#1a1a1a]/45 line-through" : "text-[#1a1a1a]"}`}>{c.title}</p>
                              <p className="mt-0.5 text-[11px] text-[#1a1a1a]/45">📍 {c.where}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* upgrade CTA */}
        <Reveal delay={0.15}>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-[#F5A623]/40 bg-[#F5A623]/[0.07] p-5">
            <div className="flex items-start gap-3 flex-1">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#F5A623] text-[#2B2B2B] shrink-0"><Crown size={18} /></span>
              <p className="text-sm text-[#2B2B2B]/75">
                <span className="font-semibold text-[#2B2B2B]">Supervisor Mode is a DriveAssure Pro feature.</span>{" "}
                Upgrade to unlock the supervisor dashboard for your school.
              </p>
            </div>
            <a
              href="#pricing"
              data-testid="supervisor-upgrade-cta"
              className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#2B2B2B] px-6 py-3 text-sm font-semibold text-white hover:bg-black transition-colors"
            >
              Upgrade to Pro
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
